// The status poll, kept free of app imports so it can be tested with fakes.
import { companyAlertsDefaults } from "./defaults.js";

export const commitUnreadAlertCounts = (commit, counts) => {
  commit("initCount", counts);
  commit("initUnreadChatsCount", counts.chatCounts.unread);
  commit("initUnreadLeadsCount", counts.serviceLeads);
};

const sameCompany = (a, b) => (a == null || b == null ? a == null && b == null : Number(a) === Number(b));

// One request for every polled badge, doubling as the session check: a company switched elsewhere
// reloads this tab instead of showing that company's counts on the old company's pages. A null count
// is one the user may not read, so its badge is cleared; a missing one (an older server) is left alone.
export const createFetchStatus =
  ({ api, reloadOutOfSync }) =>
  async ({ commit, state, rootState }) => {
    try {
      const companyId = rootState.auth.user.current_company_id;
      const response = await api.get("/auth/status");
      const status = response.data.data;
      commit("setStatusPollFailing", false);
      // Switched in this tab while the request was out: the answer describes the old company.
      if (!sameCompany(rootState.auth.user.current_company_id, companyId)) {
        return status;
      }
      if (!sameCompany(companyId, status.current_company_id)) {
        reloadOutOfSync();
        return status;
      }
      const empty = companyAlertsDefaults();
      if (status.alerts !== undefined) {
        commitUnreadAlertCounts(
          commit,
          status.alerts ?? {
            companyAlertsUnreadCount: empty.unreadCount,
            chatCounts: { unread: empty.unreadChatsCount },
            serviceLeads: empty.newLeadsCount,
          },
        );
      }
      if (status.tasks !== undefined) {
        commit("initTaskCount", status.tasks ?? empty.taskCounts);
      }
      if (status.storefront_messages !== undefined) {
        commit("initProfileMessagesCount", status.storefront_messages ?? empty.profileMessagesCount);
      }
      if (status.feedback_alerts !== undefined) {
        commit("ui/setFeedbackAlertCount", status.feedback_alerts ?? 0, { root: true });
      }
      return status;
    } catch (error) {
      // Anything that is not a failed request is a bug and is left to surface.
      if (!error.isAxiosError) {
        throw error;
      }
      // An ended session (401) is cleared and reloaded by the api.js interceptor, and a rate limit
      // (429, several tabs polling at once) clears by the next tick. Any other failure keeps the
      // session and is reported once per run of failures, so a flaky connection does not raise a
      // message every poll.
      const httpStatus = error.response?.status;
      if (httpStatus !== 401 && httpStatus !== 429 && !state.statusPollFailing) {
        commit(
          "ui/setSnackbar",
          { variant: "warning", title: "Notifications", message: "Couldn't refresh notifications. We'll keep trying." },
          { root: true },
        );
        commit("setStatusPollFailing", true);
      }
      return error;
    }
  };
